// src/pages/roster/ImportModal.jsx
import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  XMarkIcon, DocumentArrowUpIcon, CheckCircleIcon,
  ExclamationTriangleIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ----
  Column name aliases — maps common spreadsheet headers to our schema
---- */
const COLUMN_MAP = {
  firstName:   ['first name', 'firstname', 'first', 'player first', 'nombre'],
  lastName:    ['last name', 'lastname', 'last', 'player last', 'apellido', 'surname'],
  jerseyNumber:['jersey', 'jersey #', 'jersey number', 'number', '#', 'num', 'numero'],
  position:    ['position', 'pos', 'posicion'],
  parentName:  ['parent name', 'parentname', 'parent', 'guardian', 'contact name', 'madre', 'padre', 'tutor'],
  parentEmail: ['email', 'parent email', 'e-mail', 'correo'],
  parentPhone: ['phone', 'parent phone', 'cell', 'mobile', 'telefono', 'celular'],
  language:    ['language', 'lang', 'idioma', 'preferred language'],
};

function detectColumn(headers, field) {
  const aliases = COLUMN_MAP[field] || [];
  return headers.find((h) =>
    aliases.includes((h || '').toLowerCase().trim())
  ) || null;
}

function normalizeLanguage(val) {
  if (!val) return 'en';
  const v = String(val).toLowerCase().trim();
  if (v.startsWith('es') || v === 'spanish' || v === 'español' || v === 'espanol') return 'es';
  return 'en';
}

function parseRows(rawRows, headers) {
  const mapping = {};
  for (const field of Object.keys(COLUMN_MAP)) {
    const detected = detectColumn(headers, field);
    if (detected) mapping[field] = detected;
  }

  return rawRows
    .filter((row) => {
      const firstName = row[mapping.firstName] || '';
      const lastName = row[mapping.lastName] || '';
      return (firstName + lastName).trim().length > 0;
    })
    .map((row) => ({
      firstName: (row[mapping.firstName] || '').trim(),
      lastName: (row[mapping.lastName] || '').trim(),
      jerseyNumber: mapping.jerseyNumber ? (row[mapping.jerseyNumber] || '').trim() || null : null,
      position: mapping.position ? (row[mapping.position] || '').trim() || null : null,
      parentName: mapping.parentName ? (row[mapping.parentName] || '').trim() || null : null,
      parentEmail: mapping.parentEmail ? (row[mapping.parentEmail] || '').trim().toLowerCase() || null : null,
      parentPhone: mapping.parentPhone ? (row[mapping.parentPhone] || '').trim() || null : null,
      language: normalizeLanguage(mapping.language ? row[mapping.language] : null),
      notes: null,
    }));
}

/* ---- Language toggle in preview ---- */
function LangToggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(value === 'en' ? 'es' : 'en')}
      className={[
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold cursor-pointer',
        value === 'es'
          ? 'bg-amber-100 text-amber-800'
          : 'bg-[#b5f03d]/20 text-[#3d6010]',
      ].join(' ')}
    >
      {value === 'es' ? '🇲🇽 ES' : '🇺🇸 EN'}
    </button>
  );
}

/* ---- Drop zone ---- */
function DropZone({ onFile, accept, label, sub }) {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    onFile(file);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, []);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className={[
        'flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-8 py-12 cursor-pointer transition-colors',
        dragging
          ? 'border-[#b5f03d] bg-[#b5f03d]/10'
          : 'border-zinc-300 dark:border-zinc-700 hover:border-[#b5f03d] hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
      ].join(' ')}
    >
      <DocumentArrowUpIcon className="h-10 w-10 text-zinc-400 mb-3" />
      <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">{label}</p>
      <p className="text-xs text-zinc-400 mt-1">{sub}</p>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFile(e.target.files[0])}
      />
    </div>
  );
}

/* ---- Main Modal ---- */
export default function ImportModal({ teamId, onImport, onClose }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('spreadsheet'); // 'spreadsheet' | 'manual'
  const [step, setStep] = useState('upload'); // 'upload' | 'review'
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');

  const handleFile = async (file) => {
    setError(null);
    setParsing(true);
    setFileName(file.name);

    try {
      const ext = file.name.split('.').pop().toLowerCase();
      let parsed = [];

      if (ext === 'csv') {
        // CSV parse
        const text = await file.text();
        const result = Papa.parse(text, { header: true, skipEmptyLines: true });
        if (result.errors.length && result.data.length === 0) {
          throw new Error(t('importModal.errCsv'));
        }
        const headers = result.meta.fields || [];
        parsed = parseRows(result.data, headers);

      } else if (['xlsx', 'xls', 'ods'].includes(ext)) {
        // Excel parse
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(ws, { defval: '' });
        const headers = rawData.length > 0 ? Object.keys(rawData[0]) : [];
        parsed = parseRows(rawData, headers);

      } else {
        throw new Error(t('importModal.errUnsupported', { ext }));
      }

      if (parsed.length === 0) {
        throw new Error(t('importModal.errNoPlayers'));
      }

      setRows(parsed);
      setStep('review');
    } catch (err) {
      setError(err.message);
    } finally {
      setParsing(false);
    }
  };

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleConfirmImport = () => {
    const valid = rows.filter((r) => r.firstName && r.lastName);
    if (valid.length === 0) {
      setError(t('importModal.errNoValid'));
      return;
    }
    onImport(valid);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={step === 'upload' ? onClose : undefined} />

      <div className="relative w-full max-w-3xl rounded-2xl bg-white dark:bg-zinc-900 shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-zinc-900 dark:text-white">
              {step === 'upload' ? t('importModal.titleUpload') : t('importModal.titleReview', { count: rows.length, file: fileName })}
            </h2>
            {step === 'upload' && (
              <p className="text-xs text-zinc-400 mt-0.5">
                {t('importModal.uploadSubtitle')}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6">

          {/* STEP 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {parsing ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <ArrowPathIcon className="h-8 w-8 text-[#5a8a1f] animate-spin" />
                  <p className="text-sm text-zinc-500">{t('importModal.reading')}</p>
                </div>
              ) : (
                <>
                  <DropZone
                    onFile={handleFile}
                    accept=".csv,.xlsx,.xls,.ods"
                    label={t('importModal.dropLabel')}
                    sub={t('importModal.dropSub')}
                  />

                  {error && (
                    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 p-4">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-700 dark:text-red-400">{t('importModal.errorTitle')}</p>
                        <p className="text-xs text-red-600 dark:text-red-300 mt-0.5">{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Column hints */}
                  <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-4">
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">
                      {t('importModal.recognized')}
                    </p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-zinc-500">
                      {['firstName', 'lastName', 'parentEmail', 'parentPhone', 'parentName', 'language', 'jersey', 'position'].map((c) => (
                        <span key={c}>
                          <strong className="text-zinc-700 dark:text-zinc-300">{t(`importModal.cols.${c}.label`)}</strong> — {t(`importModal.cols.${c}.ex`)}
                        </span>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-zinc-400">
                      {t('importModal.caseInsensitive')}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 2: Review */}
          {step === 'review' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3">
                <CheckCircleIcon className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  {t('importModal.foundReview', { count: rows.length })}
                </p>
              </div>

              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 px-4 py-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
                {/* Table header */}
                <div className="grid grid-cols-[2fr_1.5fr_2fr_1.5fr_0.75fr_auto] gap-3 px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border-b border-zinc-200 dark:border-zinc-700 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                  <span>{t('importModal.colPlayer')}</span>
                  <span>{t('importModal.colParent')}</span>
                  <span>{t('importModal.colEmail')}</span>
                  <span>{t('importModal.colPhone')}</span>
                  <span>{t('importModal.colLang')}</span>
                  <span />
                </div>

                <ul className="divide-y divide-zinc-100 dark:divide-zinc-800 max-h-[45vh] overflow-y-auto">
                  {rows.map((row, idx) => (
                    <li key={idx} className="grid grid-cols-[2fr_1.5fr_2fr_1.5fr_0.75fr_auto] gap-3 items-center px-4 py-3">
                      <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200 truncate">
                        {row.firstName} {row.lastName}
                        {row.jerseyNumber && <span className="ml-1 text-xs text-zinc-400">#{row.jerseyNumber}</span>}
                      </span>
                      <span className="text-xs text-zinc-500 truncate">{row.parentName || '—'}</span>
                      <span className="text-xs text-zinc-500 truncate">{row.parentEmail || '—'}</span>
                      <span className="text-xs text-zinc-500 truncate">{row.parentPhone || '—'}</span>
                      <LangToggle
                        value={row.language}
                        onChange={(lang) => updateRow(idx, 'language', lang)}
                      />
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        className="text-zinc-300 hover:text-red-500 transition-colors text-xs px-1"
                        title={t('importModal.removeRow')}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-xs text-zinc-400">
                {t('importModal.editLater')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50 flex-shrink-0">
          {step === 'review' ? (
            <>
              <button
                type="button"
                onClick={() => { setStep('upload'); setRows([]); setError(null); }}
                className="px-4 py-2 rounded-xl text-sm font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-colors"
              >
                {t('importModal.back')}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-colors"
                >
                  {t('importModal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmImport}
                  disabled={rows.length === 0}
                  className="px-5 py-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-sm font-bold uppercase text-[#0d1f14] disabled:opacity-60 transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {t('importModal.importN', { count: rows.length })}
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="ml-auto px-4 py-2 rounded-xl text-sm font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-colors"
            >
              {t('importModal.cancel')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
